import React, { useState } from 'react';

interface UserFolder {
  id: number;
  user_id: number;
  name: string;
  hotkey: string;
  color: string;
  created_at: string;
}

interface BulkOperationsToolbarProps {
  selectedCount: number;
  folders: UserFolder[];
  onBulkArchive: () => void;
  onBulkMoveToFolder: (folderName: string) => void;
  onClearSelection: () => void;
  onSelectAll: () => void;
  totalImages: number;
}

const BulkOperationsToolbar: React.FC<BulkOperationsToolbarProps> = ({
  selectedCount,
  folders,
  onBulkArchive,
  onBulkMoveToFolder,
  onClearSelection,
  onSelectAll,
  totalImages
}) => {
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white shadow-lg border border-gray-200 rounded-lg px-6 py-4 z-40">
      <div className="flex items-center space-x-4">
        {/* Selection Info */}
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-blue-500 rounded border-2 border-blue-600 flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-sm"></div>
          </div>
          <span className="font-medium text-gray-900">
            {selectedCount} selected
          </span>
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-gray-300"></div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          {/* Select All */}
          {selectedCount < totalImages && (
            <button
              onClick={onSelectAll}
              className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
            >
              Select All ({totalImages})
            </button>
          )}

          {/* Move to Folder */}
          <div className="relative">
            <button
              onClick={() => setShowFolderDropdown(!showFolderDropdown)}
              className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center space-x-1"
            >
              <span>📁</span>
              <span>Move to Folder</span>
              <span className="text-xs">▼</span>
            </button>

            {showFolderDropdown && (
              <div className="absolute bottom-full mb-2 left-0 bg-white border border-gray-200 rounded-lg shadow-lg min-w-48 max-h-60 overflow-y-auto">
                <div className="p-2">
                  <button
                    onClick={() => {
                      onBulkMoveToFolder('');
                      setShowFolderDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center space-x-2"
                  >
                    <div className="w-3 h-3 rounded bg-gray-400"></div>
                    <span>Ungrouped</span>
                  </button>
                  {folders.map(folder => (
                    <button
                      key={folder.id}
                      onClick={() => {
                        onBulkMoveToFolder(folder.name);
                        setShowFolderDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center space-x-2"
                    >
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: folder.color }}
                      ></div>
                      <span>{folder.name}</span>
                      {folder.hotkey && (
                        <span className="text-xs text-gray-500 font-mono">
                          {folder.hotkey}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Archive */}
          <button
            onClick={onBulkArchive}
            className="px-3 py-1.5 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            Archive Selected
          </button>

          {/* Clear Selection */}
          <button
            onClick={onClearSelection}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Close dropdown when clicking outside */}
      {showFolderDropdown && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setShowFolderDropdown(false)}
        ></div>
      )}
    </div>
  );
};

export default BulkOperationsToolbar;
